<script setup lang="ts">
import { ref } from "vue";
import { ChevronDown } from "@lucide/vue";
import Button from "@/components/Reusables/Buttons/Button.vue";

const props = defineProps<{
  headline: string;
  /**
   * The one plain sentence a reader gets without opening anything, e.g. "Reuse a connection another
   * project already has, so one model can feed several projects." Replaces the old bullet list that used
   * to cover the whole card face (RTLCA create-wizard audit, finding 30) — the bullets now live behind the
   * "What's included" disclosure below.
   */
  description: string;
  benefits?: Array<string>;
  disabled?: boolean;
  id: number;
  /**
   * Quiet marker next to the headline for the option most people pick, e.g. "The common choice" (the
   * start dialog's own vocabulary). Left unset on cards with no default to call out.
   */
  marker?: string;
  /**
   * What choosing this card actually does, e.g. "Connect a new model" — every card used to share the
   * same "Choose Option" label, which named the button rather than the choice (RTLCA create-wizard
   * audit, finding F3). Required rather than defaulted: a shared fallback would let a new card slip back
   * into the generic label this replaced.
   */
  actionLabel: string;
}>();

const emit = defineEmits<{
  chosen: [id: number];
}>();

const handleChoiceChosen = () => {
  emit("chosen", props.id);
};

/**
 * Closed by default: the plain sentence above the disclosure already says what the option is, so
 * nothing here is needed to make a first choice. Opening it is for someone who wants the full list
 * before committing (RTLCA create-wizard audit, finding 30).
 */
const detailsShown = ref(false);
</script>

<template>
  <div
    class="rounded-16 bg-slate-0 col-span-12 flex flex-col border border-slate-200 px-4 py-6 @4xl:col-span-4 @4xl:py-10"
    :class="[{ relative: disabled }]">
    <!-- Disabled overlay -->
    <div
      v-if="disabled"
      class="rounded-16 absolute inset-0 z-40 h-full w-full bg-slate-200/60"></div>

    <!-- Heading -->
    <div class="mb-6 flex flex-col items-center justify-center">
      <span
        v-if="marker"
        class="t-caption text-type-muted"
        >{{ marker }}</span
      >
      <h2 class="t-sh4 text-center">{{ headline }}</h2>

      <slot></slot>
    </div>
    <!-- Heading END -->

    <!-- Description -->
    <p class="t-small text-type-default px-2 text-center @4xl:px-4">{{ description }}</p>
    <!-- Description END -->

    <!-- What's included: the bullets relocated out from covering the card face, collapsed by default. -->
    <div class="mt-4 mb-8 grow pr-2 pl-6 @4xl:px-8">
      <button
        v-if="benefits && benefits.length > 0"
        type="button"
        class="focus-ring rounded-4 duration-fast ease-standard flex w-full items-center gap-2 py-1 text-left transition-colors hover:bg-slate-100"
        :aria-expanded="detailsShown"
        :aria-controls="`speckle-choice-benefits-${id}`"
        @click="detailsShown = !detailsShown">
        <ChevronDown
          class="icon-sm text-type-muted duration-fast ease-standard shrink-0 transition-transform"
          :class="detailsShown ? 'rotate-180' : ''"
          :stroke-width="1.5"
          aria-hidden="true" />
        <span class="t-caption-bold text-type-default">What's included</span>
      </button>

      <Transition name="fade-in">
        <ul
          v-if="detailsShown && benefits && benefits.length > 0"
          :id="`speckle-choice-benefits-${id}`"
          class="mt-2 flex flex-col gap-2">
          <li
            v-for="(benefit, i) in benefits"
            :key="i"
            class="t-xs list-outside list-disc"
            v-html="benefit"></li>
        </ul>
      </Transition>
    </div>
    <!-- What's included END -->

    <!-- Footer -->
    <div class="mt-auto flex justify-center">
      <Button
        :label="disabled ? 'Coming soon!' : actionLabel"
        :color="disabled ? 'button' : 'primary'"
        @click="handleChoiceChosen" />
    </div>
    <!-- Footer END -->
  </div>
</template>
